import React from 'react';
import { useTranslation } from 'react-i18next';
import { SideBarChart } from '../../../../ui/SideBarChart';
import { HydratedRankBarsBlock } from '../../../../../services/admin/cloudAgentTypes';
import { BlockSourceNote } from './BlockSourceNote';
import { BlockNote } from './BlockNote';
import { isSupplierLabelled } from './blockMeta';

/**
 * rankBars → SideBarChart.
 *
 * Labels are NOT always suppliers. SideBarChart resolves a label to a supplier logo by EXACT
 * name — against the Platform enum first, then `ANY_INVOICE_LOGO_MAP` — so a bar labelled with
 * anything else that happens to match renders that supplier's logo INSTEAD of its own name: the
 * wrong identity for the row, not merely the wrong icon. `showLogos` is therefore gated on the
 * supplier-labelled allowlist in `blockMeta`.
 *
 * Which all-mode computations are actually exposed (server `allRestaurantsComputations.ts`):
 *  - `restaurantsRanking` (:438) — bare `restaurant.name`, always exposed.
 *  - `restaurantsRanking` with `metric:'leakage'` (:418) and `restaurantsLeakageRanking` (:548) —
 *    both label via `leakageLabel()`, which is the BARE restaurant name unless `usedFallback`,
 *    when a " · latest count interval…" suffix happens to immunise it.
 *  - `restaurantsLeakageByProduct` (:579) is NOT exposed: its labels always carry a
 *    " · N restaurants" suffix, so no exact match is possible.
 *
 * Both of those escapes are accidental — a suffix that exists to say something else. Tidying
 * either label to a bare name would silently re-expose it, which is exactly why the fix lives
 * here in the renderer rather than in the label text.
 */
export const RankBarsBlock: React.FC<{ block: HydratedRankBarsBlock }> = ({ block }) => {
  const { t } = useTranslation();
  const data = block.data.bars.map((b) => ({ supplier: b.label, value: b.value }));
  return (
    <div>
      {/* Own horizontal scroller: long labels must never widen the page on mobile. The caption
          and source note sit OUTSIDE it, so they don't scroll away with the chart. */}
      <div className="overflow-x-auto">
        <SideBarChart
          title={block.title ?? t('cloudAgent.blockTitles.rankBars')}
          data={data}
          maxItems={data.length || 8}
          showLogos={isSupplierLabelled(block.computation)}
        />
      </div>
      <BlockNote note={block.data.note} />
      <BlockSourceNote source={block.source} />
    </div>
  );
};
